import { useMemo, useSyncExternalStore } from 'react'
import {
  accountDayKey,
  formatDateInAccountTz,
  formatDateTimeInAccountTz,
  formatInAccountTz,
  formatTimeInAccountTz,
  getAccountTimezone,
  subscribeAccountTimezone,
} from '@/lib/accountTimezone'
import { getActiveLocale, subscribeActiveLocale } from '@/lib/locale'

export interface UseAccountTimezoneReturn {
  timezone: string
  formatDate: (value: Date | string | number | null | undefined, options?: Intl.DateTimeFormatOptions) => string
  formatTime: (value: Date | string | number | null | undefined, options?: Intl.DateTimeFormatOptions) => string
  formatDateTime: (value: Date | string | number | null | undefined, options?: Intl.DateTimeFormatOptions) => string
  format: (value: Date | string | number | null | undefined, options: Intl.DateTimeFormatOptions) => string
  dayKey: (value: Date | string | number | null | undefined) => string
}

/**
 * Reactive date helpers bound to the account timezone.
 *
 * `useSyncExternalStore` is what makes a change in Settings → Geral re-render the screens
 * already mounted; without it dates would only pick up the new zone on a reload.
 */
export function useAccountTimezone(): UseAccountTimezoneReturn {
  const timezone = useSyncExternalStore(
    subscribeAccountTimezone,
    getAccountTimezone,
    getAccountTimezone
  )

  // Language changes must re-render dates too: "13/08/2026" and "08/13/2026" are the same
  // instant in the same zone, only the locale differs.
  const locale = useSyncExternalStore(subscribeActiveLocale, getActiveLocale, getActiveLocale)

  return useMemo(
    () => ({
      timezone,
      formatDate: (value, options) =>
        options
          ? formatInAccountTz(value, options, locale, timezone)
          : formatDateInAccountTz(value, undefined, locale, timezone),
      formatTime: (value, options) =>
        options
          ? formatInAccountTz(value, options, locale, timezone)
          : formatTimeInAccountTz(value, undefined, locale, timezone),
      formatDateTime: (value, options) =>
        options
          ? formatInAccountTz(value, options, locale, timezone)
          : formatDateTimeInAccountTz(value, undefined, locale, timezone),
      format: (value, options) => formatInAccountTz(value, options, locale, timezone),
      dayKey: (value) => accountDayKey(value, timezone),
    }),
    [timezone, locale]
  )
}
